import { useState, useMemo, useCallback } from 'react';

/**
 * useSortableList — ordenação cliente-side genérica para listas.
 *
 * Uso:
 *   const { sortedItems, sortKey, sortDir, toggleSort } = useSortableList(items, {
 *     initialKey: 'data',
 *     initialDir: 'asc',
 *     getters: {
 *       data: (r) => r.data,             // string dd/mm/yyyy → convertida
 *       tempo: (r) => r.tempo_minutos,   // numérico direto
 *     }
 *   });
 *
 * Se não existir getter para a chave, o hook usa `item[key]` directamente.
 * Ordena strings alfabeticamente (case-insensitive) e números numericamente.
 * Datas no formato dd/mm/yyyy são detectadas e convertidas para timestamp.
 * Horas no formato HH:MM:SS são convertidas para segundos.
 */
export function useSortableList(items, { initialKey = null, initialDir = 'asc', getters = {} } = {}) {
  const [sortKey, setSortKey] = useState(initialKey);
  const [sortDir, setSortDir] = useState(initialDir);

  const toggleSort = useCallback((key) => {
    setSortKey((prevKey) => {
      if (prevKey !== key) {
        setSortDir('asc');
        return key;
      }
      // Mesmo campo: alterna asc → desc → sem ordenação (volta ao inicial)
      setSortDir((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
      return prevKey;
    });
  }, []);

  const sortedItems = useMemo(() => {
    if (!Array.isArray(items)) return [];
    if (!sortKey) return items;

    const getter = getters[sortKey] || ((item) => item?.[sortKey]);

    const normalize = (v) => {
      if (v === null || v === undefined || v === '') return null;
      if (typeof v === 'number') return v;
      if (typeof v === 'boolean') return v ? 1 : 0;
      const s = String(v).trim();
      // Data dd/mm/yyyy
      const mData = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (mData) {
        return new Date(`${mData[3]}-${mData[2]}-${mData[1]}`).getTime();
      }
      // Hora HH:MM ou HH:MM:SS
      const mHora = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (mHora) {
        return Number(mHora[1]) * 3600 + Number(mHora[2]) * 60 + Number(mHora[3] || 0);
      }
      // Número puro (com % ou "1h 05m")
      const numOnly = s.replace('%', '').trim();
      if (/^-?\d+(\.\d+)?$/.test(numOnly)) return Number(numOnly);
      const hm = s.match(/^(\d+)h\s*(\d+)?m?/);
      if (hm) return Number(hm[1]) * 60 + Number(hm[2] || 0);
      const mOnly = s.match(/^(\d+)m\s*(\d+)?s?$/);
      if (mOnly) return Number(mOnly[1]) + Number(mOnly[2] || 0) / 60;
      return s.toLocaleLowerCase();
    };

    const arr = [...items];
    arr.sort((a, b) => {
      const va = normalize(getter(a));
      const vb = normalize(getter(b));
      // Nulos ficam sempre no fim, independentemente da direcção
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      const sa = String(va);
      const sb = String(vb);
      const cmp = sa.localeCompare(sb, 'pt', { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [items, sortKey, sortDir, getters]);

  return { sortedItems, sortKey, sortDir, toggleSort };
}

export default useSortableList;
